import {Component, Inject} from '@angular/core';
import {FormGroup, FormBuilder, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {NavigateService, FormService} from 'ngx-satoris';
import {ApiService} from 'src/app/shared/services/api.service';
import {CartService} from 'src/app/shared/services/cart.service';
import {enumToArrayObjectSelect} from 'src/app/shared/utils/enumToArrayObject';
import {DialogNewPersonComponent} from '../dialog-new-person/dialog-new-person.component';
import {format, addYears} from 'date-fns';
import {Gender} from 'src/app/shared/models/user';
import {RequestService} from 'src/app/shared/services/request.service';
import {PassFields, PassFieldsData} from 'src/app/shared/models/request';
import {ScanDocumentService} from 'src/app/shared/services/scan-document.service';
import { dateMilliFromNowValidator } from 'src/app/shared/utils/validators';

interface DialogEditPassportDatas extends PassFieldsData {
  submitted?: boolean;
  fieldsToChange?: {[key: string]: boolean}
}

@Component({
  selector: 'app-dialog-edit-passport',
  templateUrl: './dialog-edit-passport.component.html',
  styleUrl: './dialog-edit-passport.component.scss'
})
export class DialogEditPassportComponent {
  form: FormGroup;
  submitted = false;
  genderOptions: any;
  maxBirthDate = format(new Date(), 'yyyy-MM-dd');
  minExpiryDate = format(new Date(), 'yyyy-MM-dd');
  maxExpiryDate = format(addYears(new Date(), 100), 'yyyy-MM-dd');

  constructor(@Inject(MAT_DIALOG_DATA) public data: DialogEditPassportDatas,
    public cart: CartService,
    public dialogRef: MatDialogRef<DialogNewPersonComponent>,
    public nav: NavigateService,
    public forms: FormService,
    public api: ApiService,
    private formBuilder: FormBuilder,
    public request: RequestService,
    public scan: ScanDocumentService) {
    if(this.data.submitted) {
      this.submitted = true;
    }
    this.setForm();
    this.genderOptions = enumToArrayObjectSelect(Gender, '').filter((item: any) => item.name !== Gender.OTHER);
  }

  setForm() {
    this.form = this.formBuilder.group({
      [PassFields.FIRST_NAME]: [
        {value: this.data.firstName || '', disabled: this.data.fieldsToChange ? !this.data.fieldsToChange[PassFields.FIRST_NAME] : false},
        [Validators.required, Validators.pattern('^[A-Za-z\\s\\-]+$')]
      ],
      [PassFields.LAST_NAME]: [
        {value: this.data.lastName || '', disabled: this.data.fieldsToChange ? !this.data.fieldsToChange[PassFields.LAST_NAME] : false},
        [Validators.required, Validators.pattern('^[A-Za-z\\s\\-]+$')]
      ],
      [PassFields.BIRTH_DAY]: [
        {value: this.data.birthDate ? format(new Date(this.data.birthDate), 'yyyy-MM-dd') : '', disabled: this.data.fieldsToChange ? !this.data.fieldsToChange[PassFields.BIRTH_DAY] : false},
        [Validators.required, dateMilliFromNowValidator({minDateMilliFromNow: 3942000000000, maxDateMilliFromNow: 0})]
      ],
      [PassFields.DOCUMENT_NUMBER]: [
        {value: this.data.documentNumber || '', disabled: this.data.fieldsToChange ? !this.data.fieldsToChange[PassFields.DOCUMENT_NUMBER] : false},
        [Validators.required, Validators.pattern('^[A-Za-z0-9\\s\\-]+$')]
      ],
      [PassFields.SEX]: [
        {value: this.data.sex && ['male', 'female', 'm', 'f'].includes(this.data.sex.toLowerCase()) ? (this.data.sex.toLowerCase() === 'm' ? 'male' : this.data.sex.toLowerCase() === 'f' ? 'female' : this.data.sex.toLowerCase()) : '', disabled: this.data.fieldsToChange ? !this.data.fieldsToChange[PassFields.SEX] : false},
        [Validators.required]
      ],
      [PassFields.NATIONALITY]: [
        {value: this.data.nationality || '', disabled: this.data.fieldsToChange ? !this.data.fieldsToChange[PassFields.NATIONALITY] : false},
        [Validators.required]
      ],
      [PassFields.EXPIRATION_DATE]: [
        {value: this.data.expirationDate ? format(new Date(this.data.expirationDate), 'yyyy-MM-dd') : '', disabled: this.data.fieldsToChange ? !this.data.fieldsToChange[PassFields.EXPIRATION_DATE] : false},
        this.data.expirationDate ? [
          Validators.required,
          dateMilliFromNowValidator({minDateMilliFromNow: 0, maxDateMilliFromNow: 315576000000})
        ] : []
      ]
    });
  }

  submit() {
    this.submitted = true;
    if(this.forms.getFormErrors(this.form).length < 1) {
      this.submitted = false;
      const updatedData: PassFieldsData = {};
      if(this.form.get(PassFields.FIRST_NAME)?.value !== this.data.firstName) {
        updatedData.firstName = this.form.get(PassFields.FIRST_NAME)?.value;
      }
      if(this.form.get(PassFields.LAST_NAME)?.value !== this.data.lastName) {
        updatedData.lastName = this.form.get(PassFields.LAST_NAME)?.value;
      }
      if(this.form.get(PassFields.BIRTH_DAY)?.value !== this.data.birthDate) {
        const formBirthDate = format(new Date(this.form.get(PassFields.BIRTH_DAY)?.value), 'yyyy-MM-dd');
        const dataBirthDate = this.data.birthDate ? format(new Date(this.data.birthDate), 'yyyy-MM-dd') : '';
        if(formBirthDate !== dataBirthDate) {
          updatedData.birthDate = this.form.get(PassFields.BIRTH_DAY)?.value;
        }
      }
      if(this.form.get(PassFields.DOCUMENT_NUMBER)?.value !== this.data.documentNumber) {
        updatedData.documentNumber = this.form.get(PassFields.DOCUMENT_NUMBER)?.value.toUpperCase();
      }
      if(this.form.get(PassFields.SEX)?.value?.toLowerCase() !== this.data.sex?.toLowerCase()) {
        updatedData.sex = this.form.get(PassFields.SEX)?.value;
      }
      if(this.form.get(PassFields.NATIONALITY)?.value !== this.data.nationality) {
        updatedData.nationality = this.form.get(PassFields.NATIONALITY)?.value;
      }
      if(this.data.expirationDate) {
        const formExpirationDate = format(new Date(this.form.get(PassFields.EXPIRATION_DATE)?.value), 'yyyy-MM-dd');
        const dataExpirationDate = format(new Date(this.data.expirationDate), 'yyyy-MM-dd');
        if(formExpirationDate !== dataExpirationDate) {
          updatedData.expirationDate = this.form.get(PassFields.EXPIRATION_DATE)?.value;
        }
      }
      this.closeDialog(updatedData);
    }
  }

  getMinExpirationDate() {
    return this.api.env.nationalityNoDeclaration.includes(this.form.get(PassFields.NATIONALITY)?.value) ? this.minExpiryDate : undefined;
  }

  closeDialog(modifiedData?: PassFieldsData) {
    this.dialogRef?.close(modifiedData);
  }

  protected readonly PassFields = PassFields;
  protected readonly format = format;
  protected readonly addYears = addYears;
}
